import type { MyTeam, OrgSeason } from '../../api/teams'

/**
 * Where a dashboard row goes when you click it.
 *
 * The destination depends on what the visitor may actually do there, not on
 * which table the row sits in. Sending a player to an admin screen they cannot
 * open is a 404 with extra steps; sending an owner to the read-only public page
 * makes them navigate twice to do their job.
 *
 * These mirror `admin/src/remote.tsx`, which admits owner/admin/manager to the
 * whole management surface and a team captain to the teams screens only. If
 * that gate moves, this moves with it.
 */

const ORG_ADMIN_ROLES = new Set(['owner', 'admin', 'manager'])

export interface OrgAccess {
  id: string
  slug: string
  type: string
  role: string
}

/** Owner, admin or manager of the org — the full management surface. */
export function isOrgAdmin(role: string | undefined): boolean {
  return role !== undefined && ORG_ADMIN_ROLES.has(role)
}

/**
 * A team row.
 *
 * "Your teams" lists the teams you are ON, so the row's subject is the team —
 * it goes to the team, whatever you are allowed to do to it. Managing a roster
 * is a different intent and belongs behind its own action, not smuggled into
 * the row because the viewer happens to hold the keys.
 */
export function teamHref(team: MyTeam): string {
  return `/org/${team.organization_slug}/roster/${team.slug}`
}

/** Whether to offer a manage affordance alongside a team row. */
export function canManageTeam(team: MyTeam, orgs: OrgAccess[]): boolean {
  const org = orgs.find((o) => o.id === team.organization_id)
  return team.role === 'captain' || isOrgAdmin(org?.role)
}

/**
 * A season row.
 *
 * Season administration is org-admin only — a captain has no business in it —
 * so anyone else lands on the league's public page for that competition.
 */
export function seasonHref(season: OrgSeason, orgs: OrgAccess[]): string | undefined {
  const org = orgs.find((o) => o.id === season.organization_id)
  if (!org) return undefined
  return isOrgAdmin(org.role)
    ? `/org/${org.slug}/manage/competitions/${season.id}`
    : `/org/${org.slug}/competitions/${season.slug}`
}

/**
 * Whether to show the "Your league" panel at all.
 *
 * Belonging to a league is not running one. Keying this on org TYPE alone put
 * a management panel, and a Manage link, in front of plain league members.
 */
export function runsLeague(orgs: OrgAccess[]): boolean {
  return orgs.some((o) => o.type === 'league' && isOrgAdmin(o.role))
}


/**
 * Who the "Your teams" panel belongs to.
 *
 * Derived from the teams themselves, not from the first club in the org list —
 * those can disagree. A user who administers one club but plays for another saw
 * the wrong org named above their roster.
 */
export function teamsOwner(teams: { organization_name: string }[]): string | undefined {
  const names = [...new Set(teams.map((t) => t.organization_name).filter(Boolean))]
  if (names.length === 1) return names[0]
  // Several orgs have no single owner to name; the count alone is honest.
  return undefined
}
